import styled from "styled-components";
import {
  BsCalendar3,
  BsCashStack,
  BsGraphUpArrow,
  BsLayers,
  BsPieChart,
  BsShieldCheck,
  BsWallet2,
} from "react-icons/bs";
import { CardComp } from "../..";
import FundingProgress from "../FundingProgress";
import InvestmentDetailSection from "./InvestmentDetailSection";
import moment from "moment";

const FactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const FactCard = styled(CardComp)`
  && {
    border: none;
    border-radius: 14px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    height: 100%;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    }
  }

  .icon-wrap {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: var(--color-primary-soft);
    color: var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.75rem;
    font-size: 1.15rem;
  }

  .fact-label {
    font-size: 0.75rem;
    color: #525252;
    margin-bottom: 0.25rem;
  }

  .fact-value {
    font-size: 0.95rem;
    font-weight: 600;
    color: #383838;
    line-height: 1.3;
  }
`;

const ICONS = [BsGraphUpArrow, BsCalendar3, BsLayers, BsPieChart, BsCashStack, BsWallet2, BsShieldCheck, BsCalendar3];

export default function InvestmentQuickFacts({ opportunity }) {
  if (!opportunity) return null;

  const display = (value, fallback = "—") => {
    if (value === null || value === undefined || value === "" || Number.isNaN(value)) return fallback;
    return value;
  };

  const maturityLabel = opportunity.maturityDate
    ? moment(opportunity.maturityDate).isValid()
      ? moment(opportunity.maturityDate).format("DD MMM YYYY")
      : "—"
    : "—";

  const facts = [
    {
      label: "Expected return",
      value: `${display(opportunity.expectedReturnPct, 0)}% p.a.`,
    },
    {
      label: "Duration",
      value: opportunity.durationMonths ? `${opportunity.durationMonths} months` : "—",
    },
    { label: "Minimum units", value: display(opportunity.minUnitsPurchase) },
    {
      label: "Available units",
      value: display(opportunity.availableUnits?.toLocaleString?.() ?? opportunity.availableUnits),
    },
    { label: "Investment type", value: display(opportunity.investmentType || opportunity.category) },
    { label: "Payout type", value: display(opportunity.payoutType) },
    {
      label: "Funding progress",
      value: `${display(Number.isFinite(Number(opportunity.fundedPct)) ? opportunity.fundedPct : 0, 0)}% funded`,
    },
    { label: "Maturity date", value: maturityLabel },
  ];

  return (
    <InvestmentDetailSection
      id="quick-facts"
      eyebrow="At a glance"
      title="Quick facts"
      subtitle="Key numbers to help you decide if this opportunity fits your goals."
    >
      <FactGrid>
        {facts.map((fact, index) => {
          const Icon = ICONS[index % ICONS.length];
          return (
            <FactCard key={fact.label} cardClassName="h-100">
              <div className="icon-wrap" aria-hidden>
                <Icon />
              </div>
              <p className="fact-label mb-0">{fact.label}</p>
              <p className="fact-value mb-0">{fact.value}</p>
            </FactCard>
          );
        })}
      </FactGrid>
      <CardComp cardClassName="border-0 shadow-sm rounded-4 mt-3">
        <FundingProgress percent={opportunity.fundedPct} />
      </CardComp>
    </InvestmentDetailSection>
  );
}
